import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {history} from 'client/history';
import {getModelForResourceType} from 'resources/modelRegistry';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import {resource} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';

import {renderMoment} from 'lib/timeUtils';
import {Anchor, Card, Text, Icon, Heading, addToast} from '@albert-io/atomic';

import SchoolSearchModal from 'components/SchoolSearchModal/SchoolSearchModal.react';

const getSchoolByIdQuery = (id) => {
  return resource('school_v5').mandarkEndpoint(['schools_v5', id]).getResource();
};

const getTeacherWithMetadataQuery = () => {
  return resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', sessionStore.getUserId()])
    .include('school_v5')
    .include('school_personnel_v1')
    .include('school_personnel_v1.school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};

const InstructionalLicenseCard = ({setIsReady, user}) => {
  const [teacher, setTeacher] = useState(null);
  const [showEditSchoolModal, setShowEditSchoolModal] = useState(false);

  const updateUserSchool = async (newSchool) => {
    try {
      // we update the passed in user because userUnderEdit might have
      // other changes (name, username) that we don't want to save along with the school
      const updatedUser = user.replaceRelationship({
        type: 'school_v5',
        relation: newSchool.getId()
      });

      await updatedUser.save();
      invalidatePartialInterest({resourcePath: ['teachers_v1', teacher.getId()]});
      setIsReady(false); // initiates re-fetch which will trigger fetchTeacherWithMetadata in useEffect

      addToast({
        message: 'Your school assocation has been updated successfully.',
        title: 'Success',
        type: 'success'
      });
    } catch (e) {
      addToast({
        message: 'There was a problem updating your school.',
        title: 'Error',
        type: 'danger'
      });
    }
  };

  useEffect(() => {
    let subscribed = true;
    const fetchTeacherWithMetadata = async () => {
      try {
        const res = await getTeacherWithMetadataQuery().getResourcePromise();
        if (subscribed) {
          setTeacher(res);
        }
      } catch (error) {
        history.pushState(null, '/error');
      }
    };
    fetchTeacherWithMetadata();
    return () => {
      subscribed = false;
    };
  }, [user]);

  if (teacher) {
    if (teacher.getSchoolPersonnel().every((item) => item.getStatus() === 'rejected')) {
      return (
        <div className='u-mar-b_10'>
          <Text paragraph>
            You are not approved at <Text bold>{teacher.getSchool().getName()}</Text> . Please reach
            out to your admin if you believe you should be approved at this school.
          </Text>
          <Text paragraph>
            Not your school?{' '}
            <Anchor as='button' onClick={() => setShowEditSchoolModal(true)}>
              Edit your school association
            </Anchor>
            .
          </Text>

          {showEditSchoolModal && (
            <SchoolSearchModal
              handleClose={() => setShowEditSchoolModal(false)}
              updateUserSchool={updateUserSchool}
              isTeacher={user.isTeacher()}
            />
          )}
        </div>
      );
    }

    return teacher
      .getMeta()
      .getSchoolLicensesInfo()
      .map((licenseInformation) => {
        const hasSchoolLicense = licenseInformation.get('has_school_license');
        const licenseStatus = licenseInformation.get('school_license_status');

        const activeLicenseAndApproved = hasSchoolLicense && licenseStatus === 'active';

        // school_license_subject_groups comes back chronological order, so look at last item
        const hasAllSubjects = licenseInformation
          .get('school_license_subject_groups')
          ?.last()
          ?.includes('All Subjects');

        let statusText = '';
        if (activeLicenseAndApproved) {
          statusText = 'You have full access to the included subjects.';
        } else if (!licenseInformation || !hasSchoolLicense) {
          statusText = 'Your school does not have a license.';
        } else if (!licenseStatus) {
          statusText = (
            <>
              You do not have access to this school&apos;s license because you are pending approval
              at this school.{' '}
              <Anchor
                underlined
                inheritColor
                target='_blank'
                rel='noopener noreferrer'
                href='https://help.albert.io/en/articles/4333411-what-should-i-do-if-i-am-pending-admin-approval'
              >
                How do I get approved?
              </Anchor>
            </>
          );
        } else {
          statusText =
            'You do not have access to this content because your school has an inactive license.';
        }

        const currentSchool = getSchoolByIdQuery(licenseInformation.get('school_id'));
        return (
          <Card
            border='none'
            className={classnames('u-mar-b_3', {
              'license-card__active': activeLicenseAndApproved,
              'license-card__inactive': !activeLicenseAndApproved
            })}
          >
            <div
              className={classnames('license-card__license-status u-pad_2 u-display_flex', {
                'u-bgc_green-600': activeLicenseAndApproved,
                'u-bgc_red-500': !activeLicenseAndApproved
              })}
            >
              <Icon
                icon={['far', activeLicenseAndApproved ? 'check' : 'exclamation-triangle']}
                className='u-mar-r_2'
              />
              <Text bold color='white' size='m'>
                {statusText}
              </Text>
            </div>
            <div className='u-pad_3'>
              <Heading as='h3' size='s' className='license-card__school-name u-mar-b_0'>
                {currentSchool.getName()}
              </Heading>
              <Text as='address' size='s' emphasis='lowest' bold className='u-font-style_normal'>
                {currentSchool.getAddressLine1()}, {currentSchool.getCity()},{' '}
                {currentSchool.getState()} {currentSchool.getPostalCode()}
              </Text>
              {licenseInformation.get('school_license_end_date') ? ( // TODO: this probably needs to be changed
                <div className='license-card__subscriptions-metadata license-card__subscriptions-metadata--border u-mar-t_3 u-display_flex u-flex-direction_column u-justify-content_space-between'>
                  {hasAllSubjects ? (
                    <div className='license-card__subscriptions-metadata__metadata-item u-display_flex u-flex-direction_column'>
                      <Text bold size='s' emphasis='high' className='u-mar-b_1'>
                        Subjects included:
                      </Text>
                      <Text size='s' emphasis='high'>
                        All subjects on Albert
                      </Text>
                    </div>
                  ) : null}
                  <div className='license-card__subscriptions-metadata__metadata-item u-display_flex u-flex-direction_column'>
                    <Text bold size='s' emphasis='high' className='u-mar-b_1'>
                      License type:
                    </Text>
                    <Text size='s' emphasis='high' className='u-text-transform_capitalize'>
                      {licenseInformation.get('school_license_type')}
                    </Text>
                  </div>
                  <div className='license-card__subscriptions-metadata__metadata-item u-display_flex u-flex-direction_column'>
                    <Text bold size='s' emphasis='high' className='u-mar-b_1'>
                      Expiration:
                    </Text>
                    <Text size='s' emphasis='high'>
                      {renderMoment(
                        licenseInformation.get('school_license_end_date'),
                        'dateFormal'
                      )}
                    </Text>
                  </div>
                </div>
              ) : null}
            </div>
          </Card>
        );
      });
  }
  return null;
};

InstructionalLicenseCard.propTypes = {
  setIsReady: PropTypes.func,
  user: PropTypes.instanceOf(getModelForResourceType('user_v2'))
};

export default InstructionalLicenseCard;
